/**
 * Tiny inline-SVG icon set. Matches the stroke style from the wireframe
 * (1.8px round-cap / round-join) so visual weight stays consistent.
 *
 * Use:
 *   import { icon, I } from '../components/icons.js';
 *   element.innerHTML = icon(I.play);                  // 22px default
 *   element.innerHTML = icon(I.heart, { size: 14, fill: true });
 */

export function icon(path, { size = 22, fill = false } = {}) {
  const stroke = fill
    ? 'fill="currentColor" stroke="none"'
    : 'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"';
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${stroke} style="display:block;margin:0 auto">${path}</svg>`;
}

// Outline icons (use with fill: false — default).
export const I = {
  back: '<polyline points="15 18 9 12 15 6"/>',
  play: '<polygon points="6 4 20 12 6 20"/>',
  wallet: '<rect x="3" y="6" width="18" height="13" rx="2.5"/><path d="M3 10h18"/>',
  trophy:
    '<path d="M7 4h10v3a5 5 0 0 1-10 0z"/><path d="M7 5H4v1a3 3 0 0 0 3 3"/><path d="M17 5h3v1a3 3 0 0 1-3 3"/><path d="M9 20h6M12 14v6"/>',
  user: '<circle cx="12" cy="8.5" r="3.4"/><path d="M5.5 20a6.5 6.5 0 0 1 13 0"/>',
  users: '<circle cx="9" cy="8" r="3.2"/><path d="M3 20a6 6 0 0 1 12 0"/><circle cx="17" cy="9.5" r="2.6"/><path d="M16 20a5 5 0 0 1 5-5"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M4 7l8 6 8-6"/>',
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  copy:
    '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  share:
    '<path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>',
  bank: '<path d="M3 9.5 12 4l9 5.5"/><path d="M5 21h14"/><path d="M6 11v8M10 11v8M14 11v8M18 11v8"/>',
  lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
  arrowUp: '<line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>',
  arrowDown: '<line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>',
  flame:
    '<path d="M13 2c.6 3.2-1 4.3-2.1 5.4C9.6 8.6 8 10 8 12.6A4 4 0 0 0 16 13c0-1.8-.9-3-1.4-3.7.2.9-.4 1.6-1 1.6.1-3-.2-6-.6-8.9z"/>',
  gem: '<path d="M6 3h12l3.2 5.2L12 21 2.8 8.2z"/>',
  heart:
    '<path d="M12 20.6C5.5 15.4 4.5 11.7 4.5 9.1 4.5 7 6.2 5.4 8.2 5.4c1.5 0 2.8.8 3.4 2 .6-1.2 1.9-2 3.4-2 2 0 3.7 1.6 3.7 3.7 0 2.6-1 6.3-7.1 11.5z"/>',
  bolt: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  cog: '<circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.4l1.9-1.5-1.8-3.1-2.3.8a7 7 0 0 0-2.4-1.4L14 3h-4l-.3 2.4a7 7 0 0 0-2.4 1.4l-2.3-.8L3.2 9.1l1.9 1.5A7 7 0 0 0 5 12c0 .5 0 .9.1 1.4l-1.9 1.5 1.8 3.1 2.3-.8a7 7 0 0 0 2.4 1.4L10 21h4l.3-2.4a7 7 0 0 0 2.4-1.4l2.3.8 1.8-3.1-1.9-1.5c.1-.5.1-.9.1-1.4z"/>',
  bell: '<path d="M18 16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2c1.3 0 1.5-1.2 1.5-2.5V10a4.5 4.5 0 0 1 9 0v3.5c0 1.3.2 2.5 1.5 2.5z"/><path d="M10 21a2 2 0 0 0 4 0"/>',
  // Chat / message speech bubble — lifted from Triviana_Wireframes.html
  // so the Home top-bar icon + notification centre + inbox appbar all
  // render the same glyph.
  chat: '<path d="M21 14.5a3.5 3.5 0 0 1-3.5 3.5H9l-5 3.5V6.5A3.5 3.5 0 0 1 7.5 3h10A3.5 3.5 0 0 1 21 6.5z"/>',
  send: '<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>',
  search: '<circle cx="11" cy="11" r="7"/><line x1="20" y1="20" x2="16.65" y2="16.65"/>',
  // Crossed swords for the duel-cluster notification types.
  swords:
    '<path d="M14.5 17.5 21 21l-2-3.5-3-3"/><path d="M16 13 21 3l-7 6"/><path d="M9.5 17.5 3 21l2-3.5 3-3"/><path d="M8 13 3 3l7 6"/><path d="m12 14-3 3 1 1 3-3"/><path d="m12 14 3 3-1 1-3-3"/>',
  // Pair of users — friends graph notifications.
  friends:
    '<circle cx="9" cy="8" r="3.2"/><path d="M3 20a6 6 0 0 1 12 0"/><circle cx="17" cy="9.5" r="2.6"/><path d="M16 20a5 5 0 0 1 5-5"/>',
  // Tilted eraser block — matches the home HUD glyph. Used for the
  // eraser_awarded notification type.
  eraser:
    '<path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/>',
  // 50/50 power-up — a stylised target-style ring with a half-fill
  // dividing line. Used for the home HUD chip + the
  // fiftyfifty_awarded / fiftyfifty_referral_earned notifications.
  fiftyfifty:
    '<circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="21"/><path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"/>',
  star: '<polygon points="12 2 15 9 22 10 17 15 18 22 12 19 6 22 7 15 2 10 9 9 12 2"/>',
  chevronRight: '<polyline points="9 6 15 12 9 18"/>',
  refresh:
    '<polyline points="23 4 23 10 17 10"/><path d="M20.5 14A8.5 8.5 0 1 1 5 8.5"/>',
};
